import React, {Fragment} from 'react';
import {createRoot} from 'react-dom/client';

import {createBrowserRouter, Outlet, RouterProvider} from "react-router-dom";

import {ErrorBoundary} from "react-error-boundary";
import {ModalContainer} from "react-modal-global";
import Modal from "react-modal";

import {QueryClient, QueryClientProvider} from "@tanstack/react-query";

import Header from "@root/Components/Header/Header";

import VSFallbackComponent from "root/FallbackComponent";

import {ErrorModal} from '@root/Common/ErrorHandling/ErrorModal' ;
import "@root/Common/ErrorHandling/ModalSeven360.scss";
import '@root/Common/ErrorHandling/ModalLayout.scss' ;
import '@ehcp/css/root.scss';

import AppContainer from "root/Components/Sidebar/AppContainer";
import SidebarMenu from "@ehcp/Components/Sidebar/SidebarMenu";

import {SessionUserProvider, SystemProvider} from "@ehcp/Contexts" ;

import {lazyRoute} from "root/Common/Routing/lazyRoute";
import HomePage from "@ehcp/Components/Pages/Home/HomePage";
import LoginForm from "root/Login/LoginForm";

const queryClient = new QueryClient()

Modal.setAppElement('#app');

import {headerLogoNorthumberland, loginParams, passportNorthumberlandMenu} from "@ehcp/SystemConstants";
import ProviderVariantProvider from "root/contexts/ProviderVariantProvider";
import IdleTimeoutWrapper from "root/Timeout/IdleTimeoutWrapper";

const MyApp = ({dataset}) =>
{
    const PageLayout = () => (
        <SystemProvider dataset={dataset}>
            <SessionUserProvider dataset={dataset}>
                <ProviderVariantProvider dataset={dataset}>
                    <IdleTimeoutWrapper logout_url='/?logout&logoutreason=timeout' >
                        <QueryClientProvider client={queryClient}>
                            <Header headerLogoDetails={headerLogoNorthumberland} menuDescriptor={passportNorthumberlandMenu}/>
                            <AppContainer columns={2}>
                                <ErrorBoundary FallbackComponent={VSFallbackComponent}>
                                    <SidebarMenu key='sbm'/>
                                    <Outlet key='oll'/>
                                </ErrorBoundary>
                            </AppContainer>
                            { /* <Footer/> */}
                        </QueryClientProvider>
                    </IdleTimeoutWrapper>
                </ProviderVariantProvider>
            </SessionUserProvider>
        </SystemProvider>
    );

    const LoginPageLayout = () => (
        <SystemProvider dataset={dataset}>
            <SessionUserProvider dataset={dataset}>
                <QueryClientProvider client={queryClient}>
                    <Outlet key='oll'/>
                </QueryClientProvider>
            </SessionUserProvider>
        </SystemProvider>
    );

    const PageElement = +(dataset.logged_in) === 1 ? PageLayout : LoginPageLayout;

    const HomeComponent = +(dataset.logged_in) === 1 ? HomePage : LoginForm

    const router = createBrowserRouter([
        {
            path: '/',
            element: <PageElement/>,
            children: [
                {
                    path: "",
                    element: <HomeComponent/>,
                },
                {
                    path: "login",
                    element: <LoginForm {...dataset} params={loginParams}/>,
                },
                {
                    path: "home",
                    lazy: lazyRoute(() => import("./Components/Pages/Home/HomePage2"), 'HomePage2'),
                },
                {
                    path: "admin",
                    element: <Outlet/>,    /* As this is a parent, we need an Outlet to render the child routes */
                    children: [
                        {
                            path: "",
                            lazy: lazyRoute(() => import("./Components/Pages/Admin/EHCPAdminPage"), 'EHCPAdminPage'),
                        },
                        {
                            path: "staff/:enc_user_id",
                            lazy: lazyRoute(() => import("./Components/Pages/StaffDetails/StaffDetailsPage"), 'SchoolStaffDetailsPage'),
                        },
                        {
                            path: "groups/:enc_group_id",
                            lazy: lazyRoute(() => import("../Groups/GroupMembers/GroupMembersPage"), 'GroupMembersPage'),
                        },
                        {
                            path: "learners/:enc_learner_id",
                            lazy: lazyRoute(() => import("./Components/Pages/LearnerDetails/LearnerDetailsPage"), 'LearnerDetailsPage'),
                        },
                        {
                            path: "school/:enc_provider_id",
                            lazy: lazyRoute(() => import("./Components/Pages/Admin/Schools/EHCPSchoolPage"), 'EHCPSchoolPage'),
                        },
                        {
                            path: "school/:enc_provider_id/staff/:enc_user_id",
                            lazy: lazyRoute(() => import("./Components/Pages/Admin/Schools/SchoolStaff/SchoolStaffDetails/SchoolStaffDetailsPage"), 'SchoolStaffDetailsPage'),
                        },

                    ]
                },
                {
                    path: "panels",
                    element: <Outlet/>,    /* As this is a parent, we need an Outlet to render the child routes */
                    children: [
                        {
                            path: "",
                            lazy: lazyRoute(() => import("./Components/Pages/Panels/PanelPage"), 'PanelPage'),
                        },
                        {
                            path: ":enc_panel_meeting_id",
                            lazy: lazyRoute(() => import("./Components/Pages/Panels/PanelDetails/PanelDetailPage"), 'PanelDetailPage'),
                        },
                        {
                            path: ":enc_panel_meeting_id/case/:enc_wi_panel_meeting_id",
                            lazy: lazyRoute(() => import("./Components/Pages/Panels/PanelDetails/./EHCPMeetingPanelCaseDetailPage"), 'EHCPMeetingPanelCaseDetailPage'),
                        },
                        {
                            path: ":enc_panel_meeting_id/user/:enc_user_id",
                            lazy: lazyRoute(() => import("./Components/Pages/Panels/PanelDetails/PanelDetailUserPage"), 'PanelDetailUserPage'),
                        },
                        {
                            path: ":enc_panel_meeting_id/user/:enc_user_id/case/:enc_panel_meeting_outcome_id",
                            lazy: lazyRoute(() => import("./Components/Pages/Panels/PanelDetails/ConsultantPanelCaseDetailPage"), 'ConsultantPanelCaseDetailPage'),
                        },
                    ]
                },
                {
                    path: "applications",
                    element: <Outlet/>,    /* As this is a parent, we need an Outlet to render the child routes */
                    children: [
                        {
                            path: "",
                            lazy: lazyRoute(() => import("./Components/Pages/Applications/ApplicationPage"), 'ApplicationPage'),
                        },
                        {
                            path: "toassess/:enc_ehcp_application_id",
                            lazy: lazyRoute(() => import("./Components/Pages/ToAssess/CaseDetailToAssessPage"), 'CaseDetailToAssessPage'),
                        },
                        {
                            path: ":enc_ehcp_application_id",
                            lazy: lazyRoute(() => import("./Components/Pages/CaseDetail/CaseDetailPage"), 'CaseDetailPage'),
                        },
                    ]
                },
                {
                    path: "all",
                    element: <Outlet/>,    /* As this is a parent, we need an Outlet to render the child routes */
                    children: [
                        {
                            path: "",
                            lazy: lazyRoute(() => import("./Components/Pages/AllApplications/AllApplicationsPage"), 'AllApplicationsPage'),
                        },
                    ]
                },
                {
                    path: "testy",
                    element: <Outlet/>,    /* As this is a parent, we need an Outlet to render the child routes */
                    children: [
                        {
                            path: "",
                            lazy: lazyRoute(() => import("./Components/Pages/Test/TestPage"), 'TestPage'),
                        },
                    ]
                },
                {
                    path: "coordinatorqa",
                    lazy: lazyRoute(() => import("./Components/Pages/CoordinatorQA/CoordinatorQAPage"), 'CoordinatorQAPage'),
                },
                {
                    path: "dashboard",
                    element: <Outlet/>,    /* As this is a parent, we need an Outlet to render the child routes */
                    children: [
                        {
                            path: "",
                            lazy: lazyRoute(() => import("./Components/Pages/Dashboard/EHCPDashboardPage"), 'EHCPDashboardPage'),
                        },
                        {
                            path: "applications",
                            lazy: lazyRoute(() => import("./Components/Pages/Dashboard/CaseList/DashboardCaseListPage"), 'DashboardCaseListPage'),
                        }
                    ]
                },
                {
                    path: "pending",
                    lazy: lazyRoute(() => import("./Components/Pages/PendingPanelAssignment/PendingPanelAssignmentPage"), 'PendingPanelAssignmentPage'),
                },
                {
                    path: "ratify",
                    lazy: lazyRoute(() => import("./Components/Pages/ToRatify/ToRatifyPage"), 'ToRatifyPage'),
                },
                {
                    path: "assess",
                    lazy: lazyRoute(() => import("./Components/Pages/ToAssess/ToAssessPage"), 'ToAssessPage'),
                },
                {
                    path: "history",
                    lazy: lazyRoute(() => import("./Components/Pages/COSAApplicationHistory/COSAApplicationHistoryPage"), 'COSAApplicationHistoryPage'),
                },

            ]
        }
    ]);

    return (
        <Fragment>
            <RouterProvider router={router}/>
            <ModalContainer className='modal-seven360' controller={ErrorModal}/>
        </Fragment>
    )
}

const container = document.getElementById('app');

const atts = container.dataset;

const root = createRoot(container);
root.render(<MyApp dataset={atts}/>);
